import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';

import apiService from '../services/api';
import { TrustedDevice } from '../types/api';
import Logger from '../utils/logger';
import { Button } from '../components/ui';
import { ComputerDesktopIcon, TrashIcon } from '@heroicons/react/24/outline';

/**
 * Settings page : Trusted devices management.
 *
 * Liste les appareils qui ont valide l'email verification — ils peuvent se
 * connecter sans re-demander le code. Permet de :
 * - revoke un appareil individuellement
 * - revoke tous les autres appareils (utile si compte compromis)
 */
const TrustedDevicesPage: React.FC = () => {
  const { t, i18n } = (useTranslation as any)('auth');
  const [devices, setDevices] = useState<TrustedDevice[]>([]);
  const [loading, setLoading] = useState(true);
  const [revokingId, setRevokingId] = useState<string | null>(null);
  const [revokingAll, setRevokingAll] = useState(false);

  const fetchDevices = async () => {
    setLoading(true);
    try {
      const response = await apiService.listTrustedDevices();
      setDevices(response.devices);
    } catch (error) {
      Logger.error('Failed to load trusted devices', error);
      toast.error(t('devices.loadError'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDevices();
  }, []);

  const onRevoke = async (deviceId: string) => {
    if (!window.confirm(t('devices.confirmRevoke') as string)) return;
    setRevokingId(deviceId);
    try {
      await apiService.revokeTrustedDevice(deviceId);
      toast.success(t('devices.revokeSuccess'));
      await fetchDevices();
    } catch (error) {
      Logger.error('Failed to revoke device', error);
      toast.error(t('devices.revokeError'));
    } finally {
      setRevokingId(null);
    }
  };

  const onRevokeAll = async () => {
    if (!window.confirm(t('devices.confirmRevokeAll') as string)) return;
    setRevokingAll(true);
    try {
      const result = await apiService.revokeAllOtherDevices();
      toast.success(t('devices.revokeAllSuccess', { count: result.revoked_count }));
      await fetchDevices();
    } catch (error) {
      Logger.error('Failed to revoke all other devices', error);
      toast.error(t('devices.revokeAllError'));
    } finally {
      setRevokingAll(false);
    }
  };

  const formatDate = (iso: string): string => {
    try {
      const d = new Date(iso);
      return d.toLocaleDateString(i18n.language, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return iso;
    }
  };

  const otherDevicesCount = devices.filter((d) => !d.is_current).length;

  return (
    <div className="max-w-3xl mx-auto px-4 py-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-text-primary">{t('devices.title')}</h1>
          <p className="mt-1 text-sm text-text-secondary">{t('devices.subtitle')}</p>
        </div>
        {otherDevicesCount > 0 && (
          <Button
            variant="secondary"
            size="sm"
            onClick={onRevokeAll}
            loading={revokingAll}
            disabled={revokingAll}
          >
            {t('devices.revokeAll')}
          </Button>
        )}
      </div>

      {loading ? (
        <div className="text-center py-12 text-text-secondary">{t('devices.loading')}</div>
      ) : devices.length === 0 ? (
        <div className="text-center py-12 text-text-secondary">{t('devices.noDevices')}</div>
      ) : (
        <ul className="space-y-3">
          {devices.map((d) => (
            <li
              key={d.id}
              className="bg-surface-2 rounded-card p-4 shadow-card flex items-start gap-4"
            >
              <ComputerDesktopIcon className="w-8 h-8 text-text-tertiary flex-shrink-0 mt-1" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-text-primary">
                    {d.label || t('devices.unknownDevice')}
                  </span>
                  {d.is_current && (
                    <span className="text-xs bg-green-500/12 text-green-400 px-2 py-0.5 rounded">
                      {t('devices.current')}
                    </span>
                  )}
                </div>
                <div className="mt-1 text-xs text-text-tertiary space-y-0.5">
                  <div>
                    {t('devices.lastSeen', { when: formatDate(d.last_seen_at) })}
                  </div>
                  {d.ip_last && <div>IP: {d.ip_last}</div>}
                  <div>
                    {t('devices.expiresOn', { when: formatDate(d.expires_at) })}
                  </div>
                </div>
              </div>
              {!d.is_current && (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => onRevoke(d.id)}
                  loading={revokingId === d.id}
                  disabled={revokingId === d.id}
                  className="flex-shrink-0"
                >
                  <TrashIcon className="w-4 h-4 mr-1" />
                  {t('devices.revoke')}
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default TrustedDevicesPage;
